/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { fetchShutdownBanner, downloadUserExport } from '../action/shutdown';

const ShutdownIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function formatShutdownDate(dateStr) {
  if (!dateStr) return null;
  const m = moment(dateStr, ['YYYY-MM-DD', moment.ISO_8601], true);
  return m.isValid() ? m.format('MMM D, YYYY') : null;
}

function ShutdownBanner() {
  const theme = useSelector((state) => state.theme.theme);
  const bannerRef = useRef(null);
  const [banner, setBanner] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const syncBannerHeight = useCallback(() => {
    const el = bannerRef.current;
    if (!el || !banner?.enabled) {
      return;
    }
    document.documentElement.style.setProperty('--shutdown-banner-height', `${el.offsetHeight}px`);
  }, [banner?.enabled]);

  useLayoutEffect(() => {
    if (!banner?.enabled) {
      document.body.classList.remove('has-shutdown-banner');
      document.documentElement.style.removeProperty('--shutdown-banner-height');
      return undefined;
    }

    document.body.classList.add('has-shutdown-banner');
    syncBannerHeight();

    const el = bannerRef.current;
    if (!el) {
      return () => {
        document.body.classList.remove('has-shutdown-banner');
        document.documentElement.style.removeProperty('--shutdown-banner-height');
      };
    }

    const observer = new ResizeObserver(syncBannerHeight);
    observer.observe(el);
    window.addEventListener('resize', syncBannerHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncBannerHeight);
      document.body.classList.remove('has-shutdown-banner');
      document.documentElement.style.removeProperty('--shutdown-banner-height');
    };
  }, [banner, syncBannerHeight]);

  useEffect(() => {
    let cancelled = false;
    fetchShutdownBanner()
      .then((res) => {
        if (!cancelled) {
          setBanner(res?.data?.responseData ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBanner(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownload = useCallback(async (path) => {
    setError(null);
    setDownloading(true);
    try {
      await downloadUserExport(path);
    } catch (e) {
      setError(e?.message || 'Export failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleButtonClick = useCallback(
    (button) => {
      if (button.type === 'download') {
        handleDownload(button.path || '/api/export');
      } else if (button.type === 'link' && button.url) {
        window.open(button.url, '_blank', 'noopener,noreferrer');
      }
    },
    [handleDownload],
  );

  if (!banner?.enabled) {
    return null;
  }

  const isDark = theme === 'dark';
  const shutdownLabel = formatShutdownDate(banner.shutdownDate);

  const palette = isDark
    ? {
        bg: '#1c1917',
        border: '1px solid rgba(251, 191, 36, 0.4)',
        accentBar: '#fbbf24',
        iconColor: '#fcd34d',
        text: '#fafaf9',
        meta: '#d6d3d1',
        btnPrimaryBg: '#fbbf24',
        btnPrimaryColor: '#1c1917',
        btnSecondaryColor: '#fafaf9',
        btnSecondaryBorder: '1px solid rgba(250, 250, 249, 0.3)',
        error: '#fca5a5',
      }
    : {
        bg: '#fffbeb',
        border: '1px solid rgba(217, 119, 6, 0.3)',
        accentBar: '#d97706',
        iconColor: '#b45309',
        text: '#451a03',
        meta: '#78716c',
        btnPrimaryBg: '#d97706',
        btnPrimaryColor: '#ffffff',
        btnSecondaryColor: '#451a03',
        btnSecondaryBorder: '1px solid rgba(120, 53, 15, 0.22)',
        error: '#b91c1c',
      };

  return (
    <div
      ref={bannerRef}
      className="shutdown-banner"
      role="region"
      aria-label="Service shutdown notice"
      css={{
        width: '100%',
        padding: '5px 12px',
        background: palette.bg,
        borderBottom: palette.border,
        boxSizing: 'border-box',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          background: palette.accentBar,
        },
      }}
    >
      <div
        css={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          paddingLeft: '4px',
          minHeight: '32px',
        }}
      >
        <div css={{ flexShrink: 0, display: 'flex', alignItems: 'center', color: palette.iconColor }}>
          <ShutdownIcon color={palette.iconColor} />
        </div>

        <p
          css={{
            flex: '1 1 auto',
            margin: 0,
            minWidth: 0,
            fontSize: '13px',
            lineHeight: 1.35,
            color: palette.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '@media (max-width: 640px)': {
              whiteSpace: 'normal',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            },
          }}
        >
          {banner.message}
          {shutdownLabel && (
            <span css={{ color: palette.meta, fontWeight: 500 }}> · Ends {shutdownLabel}</span>
          )}
        </p>

        <div css={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
          {(banner.buttons || []).map((button, index) => {
            const isPrimary = button.type === 'download';
            const isBusy = downloading && button.type === 'download';
            return (
              <button
                key={`${button.type}-${button.label}-${index}`}
                type="button"
                disabled={isBusy}
                onClick={() => handleButtonClick(button)}
                css={{
                  padding: '5px 11px',
                  borderRadius: '6px',
                  border: isPrimary ? 'none' : palette.btnSecondaryBorder,
                  cursor: isBusy ? 'wait' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  background: isPrimary ? palette.btnPrimaryBg : 'transparent',
                  color: isPrimary ? palette.btnPrimaryColor : palette.btnSecondaryColor,
                  opacity: isBusy ? 0.75 : 1,
                }}
              >
                {isBusy ? '…' : button.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <p
          css={{
            margin: '4px 0 0',
            paddingLeft: '26px',
            fontSize: '11px',
            color: palette.error,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

export default ShutdownBanner;
